import { forwardRef, type PropsWithChildren } from 'react';

import { Content } from './components/Content.js';
import {
  EventNotificationClient,
  isEventNotificationConfig,
} from './components/EventNotificationClient.js';
import { NotificationPermissionInfo } from './components/PermissionInfo.js';
import {
  isResourceNotificationConfig,
  ResourceNotificationClient,
} from './components/ResourceNotificationClient.js';
import { NotificationToggleButton } from './components/ToggleButton.js';
import type { NotificationSettingsProps } from './notification-settings-types.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { Flex } from '../../layouts/flex/Flex.js';

const COMPONENT_NAME = 'NotificationSettings';

/**
 * Switch function to determine which notification client should be used.
 * @internal
 */
export function NotificationProvider(
  props: PropsWithChildren<Pick<NotificationSettingsProps, 'type' | 'config'>>,
) {
  if (isResourceNotificationConfig(props.type, props.config)) {
    return (
      <ResourceNotificationClient config={props.config}>
        {props.children}
      </ResourceNotificationClient>
    );
  }

  if (isEventNotificationConfig(props.type, props.config)) {
    return (
      <EventNotificationClient config={props.config}>
        {props.children}
      </EventNotificationClient>
    );
  }

  return props.children;
}

/**
 * NotificationSettings component
 * @public
 */
export const NotificationSettings = forwardRef<
  HTMLDivElement,
  NotificationSettingsProps
>((props, ref) => {
  const {
    config,
    children,
    id,
    type,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dtRumMask,
    'data-dtrum-allow': dtRumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  return (
    <NotificationProvider type={type} config={config}>
      <Flex
        id={id}
        ref={ref}
        flexDirection="column"
        gap={12}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dtRumMask}
        data-dtrum-allow={dtRumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Content>{props.children}</Content>
        <Flex alignItems="center">
          <NotificationToggleButton />
          <NotificationPermissionInfo />
        </Flex>
      </Flex>
    </NotificationProvider>
  );
});

(
  NotificationSettings as typeof NotificationSettings & { displayName: string }
).displayName = COMPONENT_NAME;
